import type { SystemModulesResponse } from 'types';

const SECTION_FIELD_PREFIX = 'show_acct_';

/**
 * Normalises a `sys-modules` payload. The API nests the account-portal flags under `acct`
 * (`{ acct: { show_acct_* } }`); some tenants answer with the flat shape instead. Anything
 * else — an error envelope, an empty body, a string — is unusable and yields `null`, so the
 * caller can treat it exactly like a failed request.
 */
export const normalizeSystemModules = (
  data: unknown
): SystemModulesResponse | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as Record<string, unknown>;
  const { acct } = payload;

  if (acct && typeof acct === 'object' && !Array.isArray(acct)) {
    return acct as SystemModulesResponse;
  }

  const hasFlatFlags = Object.keys(payload).some((field) =>
    field.startsWith(SECTION_FIELD_PREFIX)
  );

  return hasFlatFlags ? (payload as SystemModulesResponse) : null;
};

/**
 * Session-scoped latch for the `sys-modules` endpoint.
 *
 * Tenants whose backend hasn't shipped `sys-modules` answer it with a 400 — or never answer
 * at all. The account portal must never lock a client out of a section because of that, so
 * the first failure permanently switches this session back to the pre-sys-modules behaviour:
 * the tenant's static config decides what's visible and no route is gated.
 *
 * Latching, rather than reading RTK Query's `isError` directly, matters because that flag
 * flips back to `false` for the duration of every refetch, which would re-arm the gates
 * mid-session.
 */
let hasFailed = false;

export const markSystemModulesFailed = (): void => {
  hasFailed = true;
};

export const hasSystemModulesFailed = (): boolean => hasFailed;

/** Test-only: clears the latch between cases. */
export const resetSystemModulesFallback = (): void => {
  hasFailed = false;
};
